import { useAuthStore } from '@/stores/useAuthStore';

export type SSEEventListener<T = unknown> = (
  data: T,
  rawEvent: MessageEvent | { type: string; data: unknown }
) => void;

export class SSEService {
  private url: string;
  private eventSource: EventSource | null = null;
  private listeners: Map<string, Set<SSEEventListener>> = new Map();
  private reconnectAttempts = 0;
  private backoffSchedule = [1000, 2000, 5000, 10000, 30000];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isExplicitlyClosed = false;

  public onOpenCallback?: () => void;
  public onErrorCallback?: (error: unknown) => void;

  constructor(url: string) {
    this.url = url;
  }

  public getNextBackoffDelay(): number {
    const delay =
      this.backoffSchedule[
        Math.min(this.reconnectAttempts, this.backoffSchedule.length - 1)
      ];
    this.reconnectAttempts++;
    return delay;
  }

  public resetReconnectAttempts(): void {
    this.reconnectAttempts = 0;
  }

  public getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  private getFullUrl(): string {
    let token: string | null = null;
    try {
      token = useAuthStore.getState().token;
    } catch {
      // Store not initialized
    }
    if (!token && typeof localStorage !== 'undefined') {
      token = localStorage.getItem('token');
      if (!token) {
        try {
          const authData = localStorage.getItem('chatagent_auth');
          if (authData) {
            const parsed = JSON.parse(authData);
            token = parsed?.state?.token || null;
          }
        } catch {
          // ignore
        }
      }
    }

    const baseUrl =
      typeof import.meta !== 'undefined' && import.meta.env
        ? (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '') || ''
        : '';

    let fullUrl = this.url;
    if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
      fullUrl = `${baseUrl}${fullUrl.startsWith('/') ? '' : '/'}${fullUrl}`;
    }

    if (token) {
      const separator = fullUrl.includes('?') ? '&' : '?';
      fullUrl = `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
    }

    return fullUrl;
  }

  public connect(): void {
    this.isExplicitlyClosed = false;
    if (this.eventSource) {
      this.close(false);
    }

    if (typeof EventSource === 'undefined') {
      return;
    }

    try {
      const fullUrl = this.getFullUrl();
      this.eventSource = new EventSource(fullUrl);

      this.eventSource.onopen = () => {
        this.resetReconnectAttempts();
        if (this.onOpenCallback) {
          this.onOpenCallback();
        }
      };

      this.eventSource.onerror = (err) => {
        if (this.onErrorCallback) {
          this.onErrorCallback(err);
        }
        if (!this.isExplicitlyClosed) {
          this.scheduleReconnect();
        }
      };

      this.eventSource.onmessage = (event: MessageEvent) => {
        const parsedData = this.parseData(event.data);
        const callbacks = this.listeners.get('message');
        if (callbacks) {
          callbacks.forEach((cb) => cb(parsedData, event));
        }
      };

      // Wire registered listeners
      this.listeners.forEach((_, eventType) => {
        if (eventType !== 'message') {
          this.attachSourceListener(eventType);
        }
      });
    } catch (e) {
      if (this.onErrorCallback) {
        this.onErrorCallback(e);
      }
      this.scheduleReconnect();
    }
  }

  private attachSourceListener(eventType: string): void {
    if (!this.eventSource) return;

    this.eventSource.addEventListener(eventType, (event: MessageEvent) => {
      const parsedData = this.parseData(event.data);
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        callbacks.forEach((cb) => cb(parsedData, event));
      }
    });
  }

  private parseData(data: unknown): unknown {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    }
    return data;
  }

  public addEventListener<T = unknown>(
    eventType: string,
    callback: SSEEventListener<T>
  ): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
      if (this.eventSource && eventType !== 'message') {
        this.attachSourceListener(eventType);
      }
    }
    this.listeners.get(eventType)!.add(callback as SSEEventListener);

    return () => {
      this.removeEventListener(eventType, callback);
    };
  }

  public removeEventListener<T = unknown>(
    eventType: string,
    callback: SSEEventListener<T>
  ): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.delete(callback as SSEEventListener);
    }
  }

  public scheduleReconnect(): void {
    if (this.isExplicitlyClosed || this.reconnectTimer) return;

    const delay = this.getNextBackoffDelay();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isExplicitlyClosed) {
        this.connect();
      }
    }, delay);
  }

  public reconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.connect();
  }

  public close(isExplicit = true): void {
    this.isExplicitlyClosed = isExplicit;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (isExplicit) {
      this.resetReconnectAttempts();
    }
  }

  public isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === 1; // OPEN
  }

  public getReadyState(): number {
    return this.eventSource ? this.eventSource.readyState : 2; // CLOSED
  }
}

export const createSSEService = (url: string): SSEService => {
  return new SSEService(url);
};
